// frontend/src/components/SessionExpiryWarning.js
import React, { useState, useEffect } from 'react';
import { Modal, Button, Typography, Progress, Space, Alert, message } from 'antd';
import { ExclamationCircleOutlined, ReloadOutlined, LogoutOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';

const { Text } = Typography;

const SessionExpiryWarning = () => {
    const { tokenExpiry, refreshToken, logout } = useAuth();
    const [showWarning, setShowWarning] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [isExtending, setIsExtending] = useState(false);
    const [warningDismissed, setWarningDismissed] = useState(false);

    useEffect(() => {
        if (!tokenExpiry) return;

        const checkSession = () => {
            const now = Date.now();
            const timeLeft = tokenExpiry - now;
            
            // Don't show warning if already logged out
            if (timeLeft <= 0) {
                setShowWarning(false);
                return;
            }

            setTimeRemaining(timeLeft);

            // Show warning when 10 minutes remaining (and not already shown/dismissed)
            if (timeLeft <= 10 * 60 * 1000 && !showWarning && !warningDismissed) {
                setShowWarning(true);
            }

            // Auto-hide warning if token renewed (time > 10 min)
            if (timeLeft > 10 * 60 * 1000 && showWarning) {
                setShowWarning(false);
                setWarningDismissed(false);
            }
        };

        // Check immediately
        checkSession();

        // Check every 5 seconds for smooth countdown
        const interval = setInterval(checkSession, 5000);

        return () => clearInterval(interval);
    }, [tokenExpiry, showWarning, warningDismissed]);

    const handleStayLoggedIn = async () => {
        setIsExtending(true);
        try {
            const success = await refreshToken();
            if (success) {
                setShowWarning(false);
                setWarningDismissed(false);
                message.success('Your session has been extended for another 60 minutes.');
            } else {
                throw new Error('Refresh failed');
            }
        } catch (error) {
            console.error('Error refreshing session:', error);
            message.error('Failed to extend session. Please log in again.');
            logout();
        } finally {
            setIsExtending(false);
        }
    };

    const handleLogoutNow = () => {
        setShowWarning(false);
        logout();
    };

    const handleDismiss = () => {
        setShowWarning(false);
        setWarningDismissed(true);
        // Re-show warning after 3 minutes if still not extended
        setTimeout(() => {
            setWarningDismissed(false);
        }, 3 * 60 * 1000);
    };

    // Don't render anything if no token or warning not shown
    if (!tokenExpiry || !showWarning) return null;

    // Calculate time for display
    const minutes = Math.floor(timeRemaining / 1000 / 60);
    const seconds = Math.floor((timeRemaining / 1000) % 60);
    const progressPercent = Math.max(0, (timeRemaining / (10 * 60 * 1000)) * 100);

    // Determine if time is critical (less than 3 minutes)
    const isCritical = timeRemaining < 3 * 60 * 1000;

    return (
        <Modal
            title={
                <Space>
                    <ExclamationCircleOutlined style={{ color: isCritical ? '#ff4d4f' : '#faad14', fontSize: '20px' }} />
                    <span style={{ color: isCritical ? '#ff4d4f' : 'inherit' }}>
                        {isCritical ? 'Session Expiring Immediately!' : 'Session Expiring Soon'}
                    </span>
                </Space>
            }
            open={showWarning}
            closable={false}
            maskClosable={false}
            footer={null}
            centered
            zIndex={9999}
            width={450}
        >
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <Progress
                    type="circle"
                    percent={Math.min(100, progressPercent)}
                    status={isCritical ? 'exception' : 'active'}
                    format={() => `${minutes}:${String(seconds).padStart(2, '0')}`}
                    strokeColor={
                        isCritical ? '#ff4d4f' : 
                        progressPercent < 30 ? '#faad14' : '#1890ff'
                    }
                    size={120}
                />
                <div style={{ marginTop: 16 }}>
                    <Text strong style={{ fontSize: '16px' }}>
                        Your session will expire in
                    </Text>
                </div>
                <div style={{ marginTop: 4 }}>
                    <Text style={{ fontSize: '24px', fontWeight: 'bold', color: isCritical ? '#ff4d4f' : '#1890ff' }}>
                        {minutes} minute{minutes !== 1 ? 's' : ''}
                    </Text>
                </div>
                <div style={{ marginTop: 8 }}>
                    <Text type="secondary">
                        {isCritical 
                            ? 'Your session is about to expire. Please extend it now.' 
                            : 'Click "Stay Logged In" to extend your session for another 60 minutes.'}
                    </Text>
                </div>
                <Alert
                    message={isCritical ? '⚠️ Your session will expire in less than 3 minutes!' : 'You will be automatically logged out if you don\'t extend your session.'}
                    type={isCritical ? 'error' : 'warning'}
                    showIcon
                    style={{ marginTop: 16, textAlign: 'left' }}
                />
                <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center', gap: '12px' }}>
                    <Button
                        type="primary"
                        size="large"
                        loading={isExtending}
                        onClick={handleStayLoggedIn}
                        icon={<ReloadOutlined />}
                        style={{ minWidth: 150 }}
                        danger={isCritical}
                    >
                        {isCritical ? 'Extend Now!' : 'Stay Logged In'}
                    </Button>
                    <Button
                        size="large"
                        onClick={handleLogoutNow}
                        icon={<LogoutOutlined />}
                        style={{ minWidth: 120 }}
                    >
                        Logout
                    </Button>
                </div>
                <div style={{ marginTop: 12 }}>
                    <Button
                        type="link"
                        size="small"
                        onClick={handleDismiss}
                        style={{ color: '#8c8c8c' }}
                    >
                        Dismiss (I'll save my work)
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default SessionExpiryWarning;